// Service for managing country translations
import countriesTranslations from '../data/countries.json';
import api from './api';

export const addCountryTranslation = async (countryName, translations) => {
  try {
    // Send only French name to backend API
    const response = await api.post('/organization/countries', {
      name: translations.fr, // Send French name to backend
      code: countryName // Use country name as code for now, will be updated
    });

    if (response.data.success) {
      // Save all translations to localStorage for file update
      try {
        const existingTranslations = JSON.parse(localStorage.getItem('countryTranslations') || '{}');
        const updatedTranslations = {
          ...existingTranslations,
          [countryName]: translations
        };
        
        localStorage.setItem('countryTranslations', JSON.stringify(updatedTranslations));
        
        // Generate the complete JSON for countries.json
        const completeJson = {
          countries: {
            ...countriesTranslations.countries,
            ...updatedTranslations
          }
        };
        
        console.log('Country added to backend successfully!');
        console.log('To update src/data/countries.json, copy this JSON:');
        console.log(JSON.stringify(completeJson, null, 2));
        
        return {
          success: true,
          message: 'Country added successfully! Check console for updated countries.json content.',
          data: updatedTranslations
        };
      } catch (localError) {
        console.error('Error saving translations to localStorage:', localError);
        return {
          success: true,
          message: 'Country added to backend, but translation save failed',
          data: null
        };
      }
    } else {
      return {
        success: false,
        message: response.data.message || 'Error adding country'
      };
    }
  } catch (error) {
    console.error('Error adding country:', error);
    return {
      success: false,
      message: 'Error adding country to backend',
      error: error.message
    };
  }
};

export const getCountryTranslation = (countryName, language = 'fr') => {
  try {
    // First check localStorage for new translations (temporary storage)
    const localTranslations = JSON.parse(localStorage.getItem('countryTranslations') || '{}');
    if (localTranslations[countryName]) {
      return localTranslations[countryName][language] || countryName;
    }
    
    // Then check static translations from countries.json
    const translations = countriesTranslations.countries[countryName];
    if (!translations) {
      return countryName; // Return original name if no translation found
    }
    
    return translations[language] || countryName;
  } catch (error) {
    console.error('Error getting translation:', error);
    return countryName;
  }
};

export const getAllTranslations = () => {
  return countriesTranslations;
};

// Function to reload translations from localStorage
export const reloadTranslations = () => {
  try {
    const localTranslations = JSON.parse(localStorage.getItem('countryTranslations') || '{}');
    const combinedTranslations = {
      ...countriesTranslations,
      countries: {
        ...countriesTranslations.countries,
        ...localTranslations
      }
    };
    console.log('Translations reloaded from localStorage:', combinedTranslations);
    return combinedTranslations;
  } catch (error) {
    console.error('Error reloading translations:', error);
    return countriesTranslations;
  }
};

// Function to validate localStorage structure
export const validateTranslationsFile = () => {
  try {
    const localTranslations = JSON.parse(localStorage.getItem('countryTranslations') || '{}');
    
    // Check if structure is correct
    if (typeof localTranslations !== 'object') {
      console.error('Invalid localStorage structure: not an object');
      return false;
    }
    
    console.log('localStorage structure is valid');
    return true;
  } catch (error) {
    console.error('Error validating localStorage:', error);
    return false;
  }
};

// Function to export translations as downloadable JSON
export const exportTranslations = () => {
  try {
    const localTranslations = JSON.parse(localStorage.getItem('countryTranslations') || '{}');
    const combinedTranslations = {
      countries: {
        ...countriesTranslations.countries,
        ...localTranslations
      }
    };
    
    const dataStr = JSON.stringify(combinedTranslations, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = 'countries.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log('Translations exported successfully');
    return true;
  } catch (error) {
    console.error('Error exporting translations:', error);
    return false;
  }
};

// Function to get combined translations (static + localStorage)
export const getCombinedTranslations = () => {
  try {
    const localTranslations = JSON.parse(localStorage.getItem('countryTranslations') || '{}');
    return {
      countries: {
        ...countriesTranslations.countries,
        ...localTranslations
      }
    };
  } catch (error) {
    console.error('Error getting combined translations:', error);
    return countriesTranslations;
  }
};

// Function to get localStorage translations only
export const getLocalStorageTranslations = () => {
  try {
    return JSON.parse(localStorage.getItem('countryTranslations') || '{}');
  } catch (error) {
    console.error('Error getting localStorage translations:', error);
    return {};
  }
};

// Function to generate updated countries.json content
export const generateUpdatedCountriesJson = () => {
  try {
    const localTranslations = JSON.parse(localStorage.getItem('countryTranslations') || '{}');
    const updatedCountries = {
      countries: {
        ...countriesTranslations.countries,
        ...localTranslations
      }
    };
    
    return JSON.stringify(updatedCountries, null, 2);
  } catch (error) {
    console.error('Error generating updated countries.json:', error);
    return JSON.stringify(countriesTranslations, null, 2);
  }
};

// Function to save translations to file (shows instructions)
export const saveTranslationsToFile = () => {
  try {
    const updatedJson = generateUpdatedCountriesJson();
    
    // Create downloadable file
    const dataBlob = new Blob([updatedJson], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = 'countries.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log('Updated countries.json content:');
    console.log(updatedJson);
    console.log('Copy this content to src/data/countries.json');
    
    return {
      success: true,
      message: 'Updated countries.json downloaded. Copy content to src/data/countries.json',
      content: updatedJson
    };
  } catch (error) {
    console.error('Error saving translations to file:', error);
    return {
      success: false,
      message: 'Error generating updated countries.json',
      error: error.message
    };
  }
};
